// Etiquetas, filtros, orden y paginación de Excepciones — lógica PURA, sin React.
//
// Vive aparte porque es la parte del mockup que de verdad tiene reglas: seis grupos de filtro que
// se componen en AND, ocho criterios de orden y una paginación de 30. El silo devuelve la partición
// completa del retailer en una sola llamada, así que filtrar, ordenar y paginar es del front —
// mismo criterio que Clientes VL.
import { mxDay } from '@/lib/dateHelpers';
import type {
    ExceptionRow,
    ExceptionStatus,
    ExceptionTimelineKind,
    ExceptionType,
} from '@/api/services/retail-wizard/backoffice';

/** 🔴 `LATE_CANCEL`, no `LATE_CANCELLATION`: es el valor real del enum del silo. */
export const TYPE_LABEL: Record<ExceptionType, string> = {
    CAPTURE_FIX: 'Corrección',
    LATE_CANCEL: 'Cancelación tardía',
    BACKDATING: 'Fecha retroactiva',
    PRICE_OVERRIDE: 'Precio forzado',
    EXTENDED_WARRANTY: 'Garantía ext.',
};

/**
 * El nombre largo del tipo — el del `<select>` de filtro y el de la píldora de la ficha.
 *
 * El mockup usa DOS vocabularios para el mismo tipo: uno corto en la celda de la tabla
 * (`TIPO_LABEL`) y uno largo en el detalle (`TIPO_LONG`). Se conservan los dos.
 */
export const TYPE_LONG_LABEL: Record<ExceptionType, string> = {
    CAPTURE_FIX: 'Corrección de captura',
    LATE_CANCEL: 'Cancelación tardía (fuera de 7 días)',
    BACKDATING: 'Fecha retroactiva',
    PRICE_OVERRIDE: 'Precio forzado / descuento',
    EXTENDED_WARRANTY: 'Ejecución de garantía extendida',
};

/** Los cinco tipos del corte, en el orden del mockup. `autorizacion_tope` queda ✂️ fuera. */
export const EXCEPTION_TYPE_VALUES: ExceptionType[] = [
    'CAPTURE_FIX',
    'LATE_CANCEL',
    'BACKDATING',
    'PRICE_OVERRIDE',
    'EXTENDED_WARRANTY',
];

/** Los cinco estados en el orden del mockup: Nueva · Rev · Apr · Rech · Apl. */
export const EXCEPTION_STATUS_VALUES: ExceptionStatus[] = [
    'NEW',
    'IN_REVIEW',
    'APPROVED',
    'REJECTED',
    'APPLIED',
];

/** La abreviatura de los chips del panel de filtros — el mockup usa Rev/Apr/Rech/Apl. */
export const STATUS_SHORT: Record<ExceptionStatus, string> = {
    NEW: 'Nueva',
    IN_REVIEW: 'Rev',
    APPROVED: 'Apr',
    REJECTED: 'Rech',
    APPLIED: 'Apl',
};

export const STATUS_LABEL: Record<ExceptionStatus, string> = {
    NEW: 'Nueva',
    IN_REVIEW: 'En revisión',
    APPROVED: 'Aprobada',
    APPLIED: 'Aplicada',
    REJECTED: 'Rechazada',
};

/**
 * El color dice CUÁNTA ATENCIÓN pide el caso, no solo en qué casilla está.
 *
 * 🔴 Estaba al revés: el único estado con relleno sólido era `APPLIED` — justo el que ya no
 * requiere ninguna acción —, y `NEW` se pintaba en azul informativo. En una bandeja que se lee para
 * decidir qué atender, lo que grita tiene que ser lo que espera decisión.
 *
 * Ahora: `NEW` rojo con relleno (nadie lo tomó), `IN_REVIEW` ámbar (alguien está en eso), `APPROVED`
 * verde (falta ejecutarlo), `APPLIED` azul sin relleno (cerrado y hecho) y `REJECTED` neutro
 * (cerrado sin hacer). Es la semántica del mockup, con tokens en vez de hexes para que el modo
 * oscuro siga funcionando.
 */
export const STATUS_CLASS: Record<ExceptionStatus, string> = {
    NEW: 'border-destructive bg-destructive/10 text-destructive',
    IN_REVIEW: 'border-warning text-warning',
    APPROVED: 'border-success text-success',
    APPLIED: 'border-info text-info',
    REJECTED: 'border-border text-muted-foreground',
};

/**
 * Los chips de estado del panel de filtros: cada uno lleva SU color, apagado y prendido.
 *
 * Es literal del mockup (`.flt-chip[data-flt-est=...]` y su variante `.on`) y no decorativo: el
 * chip prendido tiene que seguir diciendo de qué estado es, no volverse un bloque azul indistinto.
 */
export const STATUS_CHIP_CLASS: Record<ExceptionStatus, { on: string; off: string }> = {
    NEW: {
        on: 'border-destructive bg-destructive/10 text-destructive',
        off: 'border-destructive/60 text-destructive',
    },
    IN_REVIEW: {
        on: 'border-warning bg-warning-soft text-warning',
        off: 'border-warning/60 text-warning',
    },
    APPROVED: {
        on: 'border-success bg-success-soft text-success',
        off: 'border-success/60 text-success',
    },
    REJECTED: {
        on: 'border-muted-foreground bg-muted text-muted-foreground',
        off: 'border-border text-muted-foreground',
    },
    APPLIED: { on: 'border-info bg-info-soft text-info', off: 'border-info/60 text-info' },
};

/**
 * Cinco tipos, cinco colores distinguibles.
 *
 * ⚠️ El mockup usa ámbar para `LATE_CANCEL` **y** para `PRICE_OVERRIDE`, o sea que dos tipos se ven
 * iguales. Acá se conservan los cinco distintos. Lo que sí se corrigió es que `BACKDATING` tenía el
 * morado hardcodeado como `hsl(280 40% 92%)`: un literal fuera del sistema de tokens, ilegible en
 * modo oscuro porque no se redefine con el tema.
 */
export const TYPE_CLASS: Record<ExceptionType, string> = {
    CAPTURE_FIX: 'bg-info-soft text-info',
    LATE_CANCEL: 'bg-warning-soft text-warning',
    BACKDATING: 'bg-accent text-accent-foreground',
    PRICE_OVERRIDE: 'bg-destructive/10 text-destructive',
    EXTENDED_WARRANTY: 'bg-success-soft text-success',
};

const UNKNOWN_CLASS = 'border-border bg-muted text-muted-foreground';

/**
 * Las mismas tablas, vistas como `Partial`.
 *
 * No es un rodeo: los `Record` de arriba le dicen a TypeScript que toda llave existe, y eso es
 * cierto para los valores del enum pero **no para lo que llega por la red**. Sin esta vista, el
 * compilador considera imposible el `undefined` que en runtime sí aparece —se comprobó con un
 * `status: "ZOMBIE"` escrito a mano en DynamoDB— y borraría los fallbacks de abajo por «código
 * muerto». Declarar la duda donde de verdad está es mejor que un cast.
 */
const TYPE_LABEL_LOOKUP: Partial<Record<string, string>> = TYPE_LABEL;
const STATUS_LABEL_LOOKUP: Partial<Record<string, string>> = STATUS_LABEL;

/**
 * El tipo de cada evento de la bitácora.
 *
 * 🔴 `ExceptionTimelineEntry.kind` venía en la respuesta y **nunca se pintaba**: en un registro
 * auditable no se distinguía una solicitud de una aprobación, un rechazo o una nota de pasillo.
 */
export const TIMELINE_KIND_LABEL: Record<ExceptionTimelineKind, string> = {
    REQUEST: 'Solicitud',
    REVIEW: 'Tomado',
    NOTE: 'Nota',
    APPROVAL: 'Aprobación',
    REJECTION: 'Rechazo',
    APPLIED: 'Aplicada',
};

const TIMELINE_KIND_CLASS: Record<ExceptionTimelineKind, string> = {
    REQUEST: 'border-info text-info',
    REVIEW: 'border-warning text-warning',
    NOTE: 'border-border text-muted-foreground',
    APPROVAL: 'border-success text-success',
    REJECTION: 'border-destructive text-destructive',
    APPLIED: 'border-info text-info',
};

const TIMELINE_KIND_LOOKUP: Partial<Record<string, string>> = TIMELINE_KIND_LABEL;
const TYPE_LONG_LOOKUP: Partial<Record<string, string>> = TYPE_LONG_LABEL;

/** El nombre largo del tipo, con el valor crudo de respaldo si el silo manda uno que no conocemos. */
export function typeLongLabel(type: ExceptionType): string {
    return TYPE_LONG_LOOKUP[type] ?? type;
}

export function timelineBadge(kind: ExceptionTimelineKind): { label: string; className: string } {
    const label = TIMELINE_KIND_LOOKUP[kind];
    return label === undefined
        ? { label: kind, className: UNKNOWN_CLASS }
        : { label, className: TIMELINE_KIND_CLASS[kind] };
}

/**
 * Etiqueta y color de un tipo que puede no estar en el catálogo.
 *
 * 🔴 Un `Record` indexado con un valor que no está devuelve `undefined`, y `undefined` en un
 * className pinta una píldora **vacía, sin texto y sin borde**, sin un solo error de consola. Se
 * comprobó escribiendo `status: "ZOMBIE"` a mano en DynamoDB: la API lo devolvió con 200, la UI
 * pintó un hueco y la ficha se quedó sin ningún botón, inoperable y sin explicación.
 *
 * Un valor que no conocemos se muestra crudo: identificar no es inventar, y un dato raro visible es
 * infinitamente mejor que un dato raro invisible.
 */
export function typeBadge(type: ExceptionType): { label: string; className: string } {
    const label = TYPE_LABEL_LOOKUP[type];
    return label === undefined
        ? { label: type, className: UNKNOWN_CLASS }
        : { label, className: TYPE_CLASS[type] };
}

export function statusBadge(status: ExceptionStatus): { label: string; className: string } {
    const label = STATUS_LABEL_LOOKUP[status];
    return label === undefined
        ? { label: status, className: UNKNOWN_CLASS }
        : { label, className: STATUS_CLASS[status] };
}

/** `true` si el estado no pertenece al ciclo conocido — la ficha lo avisa en vez de quedarse muda. */
export function isUnknownStatus(status: ExceptionStatus): boolean {
    return STATUS_LABEL_LOOKUP[status] === undefined;
}

/**
 * Los seis grupos del panel de filtros del mockup, en su orden: Búsqueda · Estado · Tipo · Tienda ·
 * Solicitado por · Detectado. Todos se componen en AND; los chips de estado son OR dentro del grupo.
 *
 * 🔴 De los seis, el código solo tenía Búsqueda y una lista `types` que **nadie escribía** — código
 * muerto desde el primer día. Los otros cuatro no existían ni como control, aunque el endpoint ya
 * acepta `type`, `storeId`, `requestedBy`, `from` y `to` como query params.
 */
export interface ExcepcionesFilters {
    query: string;
    /** Chips del panel (multi-selección). Vacío = todos. */
    statuses: ExceptionStatus[];
    /** `<select>` de tipo del panel. `''` = todos. Es SIMPLE, como el mockup, no multi. */
    type: ExceptionType | '';
    /** Tienda del panel. `''` = todas. */
    storeId: string;
    /** Solicitante del panel. `''` = cualquiera. */
    requestedBy: string;
    /** Rango de solicitud, en días de calendario `YYYY-MM-DD` del piso de venta. */
    from: string;
    to: string;
    /** Pill de la barra superior; `null` = todas. Se compone en AND con los chips. */
    pill: ExceptionStatus | null;
}

export const EMPTY_EXCEPCIONES_FILTERS: ExcepcionesFilters = {
    query: '',
    statuses: [],
    type: '',
    storeId: '',
    requestedBy: '',
    from: '',
    to: '',
    pill: null,
};

const MXN = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });

/**
 * Impacto COMPLETO, para la ficha. `null` es «no se capturó», NO «cero pesos».
 *
 * 🔴 56 de las 213 filas de dev no traen `impactCents`, y `MXN.format(null / 100)` da `$0.00`: la
 * pantalla afirmaba un impacto de cero para casos donde el dato simplemente no existe.
 */
export function formatImpact(cents: number | null): string {
    return cents === null ? '—' : MXN.format(cents / 100);
}

/** Impacto ABREVIADO (`$8.9K`), para la celda angosta de la tabla. Mismo criterio con el `null`. */
export function formatImpactShort(cents: number | null): string {
    if (cents === null) return '—';
    const pesos = cents / 100;
    const sign = pesos < 0 ? '-' : '';
    const abs = Math.abs(pesos);
    if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}K`;
    return `${sign}$${String(Math.round(abs))}`;
}

/** «3 excepciones» / «1 excepción» — el `rowCount` del mockup, con su plural. */
export function pluralExceptions(count: number): string {
    return `${String(count)} ${count === 1 ? 'excepción' : 'excepciones'}`;
}

/** El mockup pagina de 30 en 30. */
export const PAGE_SIZE = 30;

export type SortKey =
    | 'exceptionId'
    | 'type'
    | 'saleId'
    | 'requestedBy'
    | 'storeId'
    | 'impact'
    | 'requestedAt'
    | 'status';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
    key: SortKey;
    direction: SortDirection;
}

/**
 * Default del mockup: la fecha de solicitud, de la más reciente a la más vieja
 * (`sortKey = 'detectada'`, `sortDir = 'desc'`).
 *
 * Es la excepción declarada a la regla de orden alfabético por defecto: una bandeja de casos
 * pendientes se lee por antigüedad, no por folio, y el mockup lo fija por escrito. Los catálogos
 * de esta pantalla que SÍ son listas de opciones —tienda, solicitante, tipo— van alfabéticos.
 */
export const DEFAULT_SORT: SortState = { key: 'requestedAt', direction: 'desc' };

/** Agrega o quita un valor de un filtro multi-selección (los chips de estado). */
export function toggleInList<T>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/** Cuántos filtros hay puestos — el contador `fltCount` del encabezado del panel. */
export function countActiveFilters(filters: ExcepcionesFilters): number {
    const fields = [
        filters.query,
        filters.type,
        filters.storeId,
        filters.requestedBy,
        filters.from,
        filters.to,
    ].filter((value) => value !== '').length;
    return filters.statuses.length + fields;
}

/**
 * El resumen del caso para la columna «Caso»: la primera línea de la justificación.
 *
 * 🔴 Antes se pintaba `row.title`, un campo que el silo **no tiene** — la columna decía literalmente
 * `·` en las 213 filas. No se inventa un título: se muestra lo que sí existe y sí lo escribió una
 * persona, que es la justificación.
 */
export function caseSummary(row: ExceptionRow): string {
    const firstLine = row.justification.split('\n', 1)[0].trim();
    return firstLine.length > 0 ? firstLine : 'Sin justificación capturada';
}

/**
 * Quién pidió la excepción, con el nombre si el silo lo congeló y el id si no.
 *
 * ⚠️ `requestedByName` viaja `null` en las 229 filas de dev: el controller pasa solo `{ actorId }`
 * a las cinco mutaciones. `nameOf` cruza contra el padrón de `retail-org` cuando el id resulta ser
 * un usuario retail; cuando no, se muestra el id crudo. Identificar no es inventar.
 */
export function requesterOf(
    row: Pick<ExceptionRow, 'requestedBy' | 'requestedByName'>,
    nameOf: (userId: string | null) => string | null = () => null,
): string {
    return row.requestedByName ?? nameOf(row.requestedBy) ?? row.requestedBy ?? 'Sin registrar';
}

/**
 * Filtra sobre lo que el silo manda DE VERDAD.
 *
 * 🔴 El haystack usaba `title`, `sellerName` y `storeName`: tres campos inexistentes que se
 * concatenaban como la cadena `"undefined"`. Efecto medido: buscar `undefined` devolvía las 213
 * filas, y buscar «Corrección», «Centro CDMX» o el id de una tienda devolvía 0.
 *
 * `storeNameOf` y `userNameOf` entran por parámetro porque ninguno de los dos nombres viene en la
 * respuesta: los cruza la pantalla contra el padrón de `retail-org`.
 */
export function filterExceptions(
    rows: ExceptionRow[],
    filters: ExcepcionesFilters,
    storeNameOf: (storeId: string | null) => string | null = () => null,
    userNameOf: (userId: string | null) => string | null = () => null,
): ExceptionRow[] {
    const query = filters.query.trim().toLowerCase();
    return rows.filter((row) => {
        if (query) {
            const haystack = [
                row.exceptionId,
                row.justification,
                TYPE_LABEL_LOOKUP[row.type] ?? row.type,
                STATUS_LABEL_LOOKUP[row.status] ?? row.status,
                row.saleId ?? '',
                row.storeId ?? '',
                storeNameOf(row.storeId) ?? '',
                requesterOf(row, userNameOf),
            ]
                .join(' ')
                .toLowerCase();
            if (!haystack.includes(query)) return false;
        }
        if (filters.statuses.length > 0 && !filters.statuses.includes(row.status)) return false;
        if (filters.type !== '' && row.type !== filters.type) return false;
        if (filters.storeId !== '' && row.storeId !== filters.storeId) return false;
        if (filters.requestedBy !== '' && row.requestedBy !== filters.requestedBy) return false;
        // Comparación por DÍA de calendario del PISO DE VENTA, no por la cadena ISO cruda:
        // `2026-08-06T18:30:00Z <= '2026-08-06'` es falso comparando enteras, y `slice(0, 10)` daría
        // el día UTC — que para una solicitud de las 19:00 de CDMX ya es el día siguiente.
        if (filters.from !== '' && mxDay(row.requestedAt) < filters.from) return false;
        if (filters.to !== '' && mxDay(row.requestedAt) > filters.to) return false;
        if (filters.pill && row.status !== filters.pill) return false;
        return true;
    });
}

/** Valor comparable por criterio de orden — string o número, nunca mezclados en la misma clave. */
function sortValue(
    row: ExceptionRow,
    key: SortKey,
    storeNameOf: (storeId: string | null) => string | null,
    userNameOf: (userId: string | null) => string | null,
): string | number {
    switch (key) {
        case 'exceptionId':
            return row.exceptionId;
        case 'type':
            return TYPE_LABEL_LOOKUP[row.type] ?? row.type;
        case 'saleId':
            return row.saleId ?? '';
        case 'requestedBy':
            return requesterOf(row, userNameOf);
        case 'storeId':
            return storeNameOf(row.storeId) ?? row.storeId ?? '';
        // El mockup ordena por VALOR ABSOLUTO del impacto: lo que importa es el tamaño del caso, no
        // si el dinero entra o sale. Un impacto sin capturar ordena como 0 — es decisión de orden,
        // no un dato a la vista: la celda sigue pintando «—».
        case 'impact':
            return Math.abs(row.impactCents ?? 0);
        case 'requestedAt':
            return row.requestedAt;
        case 'status':
            return STATUS_LABEL_LOOKUP[row.status] ?? row.status;
    }
}

export function sortExceptions(
    rows: ExceptionRow[],
    sort: SortState,
    storeNameOf: (storeId: string | null) => string | null = () => null,
    userNameOf: (userId: string | null) => string | null = () => null,
): ExceptionRow[] {
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const va = sortValue(a, sort.key, storeNameOf, userNameOf);
        const vb = sortValue(b, sort.key, storeNameOf, userNameOf);
        if (typeof va === 'string' && typeof vb === 'string') {
            return sign * va.localeCompare(vb, 'es-MX');
        }
        return sign * (Number(va) - Number(vb));
    });
}

/**
 * Al cambiar de columna se arranca ASCENDENTE en las de texto (A→Z es lo natural en un folio, un
 * tipo o un nombre) y DESCENDENTE en las de magnitud y fecha (lo más alto y lo más reciente
 * primero). Repetir la misma columna invierte la dirección — igual que `setSort()` del mockup.
 */
export function nextSort(current: SortState, key: SortKey): SortState {
    if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    }
    return { key, direction: key === 'impact' || key === 'requestedAt' ? 'desc' : 'asc' };
}

/**
 * Qué se puede hacer con la excepción según su estado.
 *
 * Solo se decide antes de aplicarse: una excepción APLICADA ya tocó datos reales, y "des-aprobarla"
 * no revierte nada — eso sería una excepción nueva. REJECTED tampoco se re-abre: se levanta otra
 * con el documento que faltaba, y así el rastro queda completo en vez de sobrescrito.
 */
export function canResolve(status: ExceptionStatus): boolean {
    return status === 'NEW' || status === 'IN_REVIEW';
}

/**
 * Tomar el caso solo sale de `NEW`: es lo que evita que dos Admin VL trabajen el mismo.
 *
 * ⚠️ El silo responde 409 `SALE_EXCEPTION_NOT_REVIEWABLE` si ya lo tomó alguien, así que el botón se
 * esconde en vez de fallar al click. Pero el guard del silo **no es atómico**: dos requests
 * simultáneos pasan los dos. El front no puede arreglar eso — solo evita generarlos (ver el guard
 * de doble click en la ficha).
 */
export function canReview(status: ExceptionStatus): boolean {
    return status === 'NEW';
}

/**
 * Marcar como aplicada solo sale de `APPROVED`.
 *
 * 🔴 Esto NO muta la venta ni re-liquida comisión: es marca de estado + bitácora. `APPROVED` es «el
 * Admin VL lo autorizó» y `APPLIED` es «alguien ya lo ejecutó por fuera» — el cierre del circuito es
 * manual hasta que exista `SaleAmendedV1`. Verificado en runtime contra `fiado-dev`: la fila de la
 * venta queda idéntica en los 21 campos.
 */
export function canApply(status: ExceptionStatus): boolean {
    return status === 'APPROVED';
}

/** `true` cuando el caso está cerrado: ninguna transición sale de acá, solo notas. */
export function isClosed(status: ExceptionStatus): boolean {
    return status === 'APPLIED' || status === 'REJECTED';
}
